<template>
  <form class="feedback-form" role="form" @submit.prevent="validateAndSubmit">
    <h5 class="feedback-form__title">
      {{ modalTitle }}
    </h5>
    <p v-if="modalSubtitle" class="feedback-form__subtitle">
      {{ modalSubtitle }}
    </p>
    <div class="feedback-form__content">
      <div class="feedback-form__group feedback-form__group--content">
        <div class="feedback-form__group-label">
          {{ contentTypeQuestion }}
        </div>
        <div class="feedback-form__stars-container">
          <RatingStars v-model="contentRating" :readOnly="isReadOnly" />
        </div>
      </div>
      <div
        v-if="instructor"
        class="feedback-form__group feedback-form__group--instructor"
      >
        <div class="feedback-form__group-label">
          {{ instructorQuestion }}
          <strong class="feedback-form__group-label-instructor">
            {{ instructor.name }}{{ !isCreated ? '?' : '' }}
          </strong>
        </div>
        <div class="feedback-form__stars-container">
          <RatingStars v-model="instructorRating" :readOnly="isReadOnly" />
        </div>
      </div>
      <div class="feedback-form__group feedback-form__group--feedback">
        <div class="feedback-form__group-label">{{ feedbackSubtitle }}</div>
        <p
          v-if="isFeedbackConfirmed"
          class="feedback-form__observations--confirmed"
        >
          “{{ observations }}”
        </p>
        <template v-if="!isFeedbackConfirmed">
          <textarea
            v-if="!isCreated || textareaVisible"
            v-model="observations"
            :placeholder="textAreaPlaceholder"
            class="feedback-form__observations"
            :disabled="isReadOnly"
          ></textarea>
          <p v-else class="feedback-form__observations--empty">
            No feedback submitted.
            <span @click="showFeedbackTextarea">Add feedback now</span>
          </p>
        </template>
      </div>
      <div class="feedback-form__group feedback-form__group--buttons">
        <button
          v-if="isCreated"
          class="btn btn--small"
          :class="{
            'btn--secondary': isSubmitButtonVisible,
          }"
          @click="closeForm"
        >
          Close
        </button>
        <button
          v-if="isSubmitButtonVisible"
          :disabled="isLoading || !submitEnabled"
          type="submit"
          class="btn btn--primary btn--small"
        >
          Submit
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { mapActions } from 'vuex'
import { format } from 'date-fns'
import RatingStars from '@/components/forms/RatingStars.vue'
import CONTSTANTS from '@/constants'

export default {
  name: 'FeedbackForm',
  inject: ['getWindowWidth'],
  components: {
    RatingStars,
  },
  props: {
    instructor: {
      type: Object,
      required: false,
    },
    rating: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      ratingId: this.rating.id,
      contentRating: this.rating.rating ? parseInt(this.rating.rating) : 0,
      instructorRating: this.rating.instructor_rating
        ? parseInt(this.rating.instructor_rating)
        : 0,
      observations: this.rating.observations || '',
      isFeedbackConfirmed: !!this.rating.confirmed,
      submitEnabled: false,
      textareaVisible: !!this.rating.observations,
    }
  },
  computed: {
    isSubmitButtonVisible() {
      return (
        !this.isCreated ||
        this.submitEnabled ||
        (!this.isFeedbackConfirmed && this.textareaVisible)
      )
    },
    contentTypeQuestion() {
      if (this.isCreated) {
        return `Your rating for this ${this.rating.model_type.replace(
          '-',
          ' '
        )}`
      } else {
        return `How would you rate this ${this.rating.model_type.replace(
          '-',
          ' '
        )}?`
      }
    },
    instructorQuestion() {
      if (this.isCreated) {
        return 'Your rating for your instructor,'
      } else {
        return 'How would you rate your instructor,'
      }
    },
    textAreaPlaceholder() {
      return `What would you like us to know about this ${this.rating.model_type.replace(
        '-',
        ' '
      )}?`
    },
    isCreated() {
      return !!this.ratingId
    },
    isReadOnly() {
      return this.isFeedbackConfirmed || this.isLoading
    },
    requirementsMet() {
      const required = [this.contentRating, this.observations]

      if (this.instructor) required.push(this.instructorRating)

      const filled = required.filter(Boolean)
      return required.length === filled.length
    },
    modalTitle() {
      const {
        CONTENT_TYPES: { LEARNING_PATH, COURSE },
      } = CONTSTANTS
      if (this.isCreated) return 'Thank you for your input!'

      switch (this.rating.model_type) {
        case COURSE:
          return 'Your input helps us improve our courses.'
        case LEARNING_PATH:
          return 'Your input helps us improve our learning paths.'
        default:
          return 'Your input helps us improve our contents.'
      }
    },
    modalSubtitle() {
      if (this.rating.modified) {
        return format(
          new Date(this.rating.modified),
          "'Submitted on' MMMM do, yyyy 'at' hh:mmaaa"
        )
      }

      return null
    },
    feedbackSubtitle() {
      if (!this.isCreated || this.textareaVisible) return 'Add feedback.'

      return 'Your feedback:'
    },
  },
  watch: {
    contentRating() {
      this.enableSubmit()
    },
    instructorRating() {
      this.enableSubmit()
    },
    observations() {
      this.enableSubmit()
    },
  },
  methods: {
    ...mapActions('content', ['createContentRating', 'updateContentRating']),
    closeForm() {
      this.$emit('close')
    },
    enableSubmit() {
      this.submitEnabled = true
    },
    disableSubmit() {
      this.submitEnabled = false
    },
    showFeedbackTextarea() {
      this.textareaVisible = true
    },
    async validateAndSubmit() {
      let payload = {
        id: this.ratingId,
        rating: this.contentRating,
        observations: this.observations,
        confirmed: this.requirementsMet,
      }
      let response

      if (this.instructor)
        payload = {
          ...payload,
          instructor: this.instructor.id,
          instructor_rating: this.instructorRating,
        }

      this.isLoading = true

      if (!payload.id) {
        response = await this.createContentRating({
          ...payload,
          model_id: this.rating.model_id,
          model_type: this.rating.model_type,
        })

        if (response) this.ratingId = response.id
      } else {
        response = await this.updateContentRating(payload)
      }

      this.isLoading = false

      if (response) {
        if (response.observations) this.showFeedbackTextarea()
        this.isFeedbackConfirmed = response.confirmed
        this.disableSubmit()
        this.$emit('save')
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_RATING_SENT, {
          content_id: this.ratingId,
          instructor_id: this.instructor?.id,
          instructor_name: this.instructor?.name,
          ...payload,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.feedback-form {
  display: flex;
  flex-direction: column;
  color: var(--white);
  height: 100%;
  &__title {
    margin: 0;

    font-feature-settings: 'clig' off, 'liga' off;

    font-size: 1.25rem;
    font-style: normal;
    font-weight: 700;
    line-height: 1.625rem; /* 130% */
  }
  &__subtitle {
    margin: 5px 0 0 0;

    color: var(--light-blue-gray);
    font-feature-settings: 'clig' off, 'liga' off;

    font-size: 0.625rem;
    font-style: normal;
    font-weight: 450;
    line-height: 0.75rem; /* 120% */
  }
  &__content {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    flex: 1;

    @include mobile {
      margin-top: 30px;
    }
  }
  &__group {
    &-label {
      margin-bottom: 5px;

      color: var(--white);
      font-feature-settings: 'clig' off, 'liga' off;

      font-family: var(--font-book);
      font-size: 0.875rem;
      font-style: normal;
      font-weight: 450;
      line-height: 1.25rem;

      &-instructor {
        font-family: var(--font-bold);
        font-weight: 700;
      }
    }

    &--instructor {
      margin-top: 25px;
    }

    &--feedback {
      margin-top: 30px;
      flex-grow: 1;

      display: flex;
      flex-direction: column;
    }

    &--buttons {
      margin-top: 20px;

      display: flex;
      flex-direction: column;
      gap: 10px;

      @include mobile {
        flex-direction: row;
        justify-content: flex-end;
        margin-top: 30px;
      }
    }
  }

  &__observations {
    flex-grow: 1;
    width: 100%;
    padding: 11px;
    overflow: auto;
    resize: none;

    border-radius: 4px;
    border: 1px solid var(--emperor-gray);
    background: var(--darker-gray);

    box-shadow: 0px 1px 2px 0px rgba(55, 65, 81, 0.08);

    color: var(--gray-nine);

    font-size: 0.75rem;
    font-style: normal;
    font-weight: 500;
    line-height: 1rem;

    &--empty,
    &--confirmed {
      margin: 0;

      color: var(--light-blue-gray);
      font-feature-settings: 'clig' off, 'liga' off;

      font-size: 0.875rem;
      font-style: normal;
      font-weight: 450;
      line-height: 1.25rem;

      span {
        color: var(--purple-pale);
        cursor: pointer;
      }
    }
  }
}
</style>
